import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createUtilityProviderReferenceId,
  getUtilityProviderInfo,
  updateUtilityProviderReferenceId,
} from 'app/services/http/agency/utilityProvider'
import * as snugNotifier from 'app/services/snugNotifier'

/**
 * Query keys for utility provider related queries
 */
export const utilityProviderQueryKeys = {
  all: ['utilityProvider'],
  info: (agencyId) => [...utilityProviderQueryKeys.all, 'info', agencyId],
  referenceId: (agencyId) => [
    ...utilityProviderQueryKeys.all,
    'referenceId',
    agencyId,
  ],
}

/**
 * Hook to fetch utility provider information for an agency
 * @param {string} agencyId - The agency ID
 * @param {object} options - Additional query options
 */
export const useUtilityProviderInfo = (agencyId, options = {}) => {
  return useQuery({
    queryKey: utilityProviderQueryKeys.info(agencyId),
    queryFn: () => getUtilityProviderInfo(agencyId),
    enabled: !!agencyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  })
}

/**
 * Hook to update utility provider reference ID
 * @param {object} options - Mutation options
 */
export const useUpdateUtilityProviderReferenceId = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ agencyId, referenceId, userId }) =>
      updateUtilityProviderReferenceId(agencyId, referenceId, userId),

    // Optimistic update for better UX
    onMutate: async ({ agencyId, referenceId, userId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: utilityProviderQueryKeys.info(agencyId),
      })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(
        utilityProviderQueryKeys.info(agencyId),
      )

      // Optimistically update the cache
      queryClient.setQueryData(
        utilityProviderQueryKeys.info(agencyId),
        (oldData) => ({
          ...oldData,
          data: {
            ...oldData?.data,
            reference_id: referenceId,
          },
        }),
      )

      // Return context with the previous data for potential rollback
      return { previousData, agencyId }
    },

    onSuccess: (data, variables) => {
      // Update cache with the actual response data
      queryClient.setQueryData(
        utilityProviderQueryKeys.info(variables.agencyId),
        (oldData) => ({
          ...oldData,
          data: {
            ...oldData?.data,
            ...data.data,
          },
        }),
      )

      // Show success notification
      snugNotifier.success('Utility provider reference ID updated successfully')

      // Call custom success handler if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables)
      }
    },

    onError: (error, variables, context) => {
      // Rollback the optimistic update on error
      if (context?.previousData) {
        queryClient.setQueryData(
          utilityProviderQueryKeys.info(context.agencyId),
          context.previousData,
        )
      }

      // Show error notification
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update utility provider reference ID'
      snugNotifier.error(errorMessage)

      // Call custom error handler if provided
      if (options.onError) {
        options.onError(error, variables, context)
      }
    },

    onSettled: (data, error, variables) => {
      // Always refetch the data after mutation completes
      queryClient.invalidateQueries({
        queryKey: utilityProviderQueryKeys.info(variables.agencyId),
      })

      // Call custom settled handler if provided
      if (options.onSettled) {
        options.onSettled(data, error, variables)
      }
    },

    ...options,
  })
}

/**
 * Hook for bulk operations or specialized reference ID updates
 * @param {object} options - Mutation options
 */
export const useUpdateReferenceIdForAllMembers = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ agencyId, referenceId, memberIds = [] }) => {
      // This could be extended to handle bulk updates if the API supports it
      return updateUtilityProviderReferenceId(agencyId, referenceId)
    },

    onSuccess: (data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: utilityProviderQueryKeys.all,
      })

      // Could also invalidate team member queries if they cache referrer IDs
      queryClient.invalidateQueries({
        queryKey: ['teamMembers', variables.agencyId],
      })

      snugNotifier.success('Reference ID updated for all team members')

      if (options.onSuccess) {
        options.onSuccess(data, variables)
      }
    },

    onError: (error, variables, context) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update reference ID for team members'
      snugNotifier.error(errorMessage)

      if (options.onError) {
        options.onError(error, variables, context)
      }
    },

    ...options,
  })
}

/**
 * Hook to create utility provider reference ID for all team members
 * @param {object} options - Mutation options
 */
export const useCreateUtilityProviderReferenceId = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ agencyId, referenceId }) =>
      createUtilityProviderReferenceId(agencyId, referenceId),

    // Optimistic update for better UX
    onMutate: async ({ agencyId, referenceId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: utilityProviderQueryKeys.info(agencyId),
      })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(
        utilityProviderQueryKeys.info(agencyId),
      )

      // Optimistically update the cache
      queryClient.setQueryData(
        utilityProviderQueryKeys.info(agencyId),
        (oldData) => ({
          ...oldData,
          data: {
            ...oldData?.data,
            reference_id: referenceId,
          },
        }),
      )

      // Return context with the previous data for potential rollback
      return { previousData, agencyId }
    },

    onSuccess: (data, variables) => {
      // Update cache with the actual response data
      queryClient.setQueryData(
        utilityProviderQueryKeys.info(variables.agencyId),
        {
          data: {
            ...data.data,
          },
        },
      )

      // Show success notification
      snugNotifier.success(
        'Utility provider reference ID created for all team members',
      )

      // Call custom success handler if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables)
      }
    },

    onError: (error, variables, context) => {
      // Rollback the optimistic update on error
      if (context?.previousData) {
        queryClient.setQueryData(
          utilityProviderQueryKeys.info(context.agencyId),
          context.previousData,
        )
      }

      // Show error notification
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create utility provider reference ID'
      snugNotifier.error(errorMessage)

      // Call custom error handler if provided
      if (options.onError) {
        options.onError(error, variables, context)
      }
    },

    onSettled: (data, error, variables) => {
      // Always refetch the data after mutation completes
      queryClient.invalidateQueries({
        queryKey: utilityProviderQueryKeys.info(variables.agencyId),
      })

      // Invalidate team member queries if they cache referrer IDs
      queryClient.invalidateQueries({
        queryKey: ['teamMembers', variables.agencyId],
      })

      // Call custom settled handler if provided
      if (options.onSettled) {
        options.onSettled(data, error, variables)
      }
    },

    ...options,
  })
}
